import { ArrowUpRight, Link2, LoaderCircle, LogOut, Sparkles, Trash2, UploadCloud, X } from "lucide-react";
import { useRef, useState } from "react";
import { api } from "../lib/api";

interface Props {
  onChanged: () => Promise<void>;
  onLogout: () => void;
  onClose: () => void;
  pushToast: (message: string, tone?: "success" | "error") => void;
}

export function AdminPanel({ onChanged, onLogout, onClose, pushToast }: Props) {
  const [files, setFiles] = useState<File[]>([]);
  const [caption, setCaption] = useState("");
  const [shareText, setShareText] = useState("");
  const [busy, setBusy] = useState<"upload" | "import" | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const addFiles = (incoming: FileList | File[]) => {
    const valid = [...incoming].filter((file) => file.type.startsWith("image/") && file.size <= 20 * 1024 * 1024);
    const merged = [...files, ...valid].filter(
      (file, index, all) => all.findIndex((candidate) => `${candidate.name}-${candidate.size}-${candidate.lastModified}` === `${file.name}-${file.size}-${file.lastModified}`) === index,
    );
    setFiles(merged.slice(0, 300));
    if (merged.length > 300) pushToast("单次任务最多选择 300 张，多出的没加入队列", "error");
    if (valid.length < [...incoming].length) pushToast("已跳过非图片或超过 20MB 的文件", "error");
  };

  const upload = async () => {
    if (!files.length) return pushToast("先选择要上传的图片", "error");
    setBusy("upload");
    setUploadProgress(0);
    const batches: File[][] = [];
    for (let index = 0; index < files.length; index += 8) batches.push(files.slice(index, index + 8));
    const succeeded = new Set<File>();
    const errors: string[] = [];
    let cursor = 0;
    let finished = 0;
    try {
      const worker = async () => {
        while (cursor < batches.length) {
          const batch = batches[cursor++];
          try {
            await api.upload(batch, caption);
            batch.forEach((file) => succeeded.add(file));
          } catch (error) {
            errors.push(error instanceof Error ? error.message : "批次上传失败");
          } finally {
            finished += batch.length;
            setUploadProgress(Math.round((finished / files.length) * 100));
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(3, batches.length) }, () => worker()));
      setFiles((current) => current.filter((file) => !succeeded.has(file)));
      if (succeeded.size === files.length) setCaption("");
      await onChanged();
      pushToast(
        errors.length ? `已上传 ${succeeded.size} 张，${files.length - succeeded.size} 张留在队列可重试` : `已收进 ${succeeded.size} 张图片`,
        errors.length ? "error" : "success",
      );
    } finally {
      setBusy(null);
      setUploadProgress(0);
    }
  };

  const importShare = async () => {
    if (!shareText.trim()) return pushToast("先粘贴分享链接或整段分享文案", "error");
    setBusy("import");
    try {
      const result = await api.importShare(shareText);
      setShareText("");
      await onChanged();
      pushToast(`已解析「${result.title}」，归档 ${result.assets} 个媒体`);
    } catch (error) {
      pushToast(error instanceof Error ? error.message : "解析失败", "error");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="admin-sheet-backdrop" onClick={onClose}>
      <aside className="admin-sheet" onClick={(event) => event.stopPropagation()} aria-label="管理工作台">
        <header>
          <div><span>PRIVATE STUDIO</span><h2>管理工作台</h2></div>
          <button onClick={onClose} aria-label="关闭管理工作台"><X /></button>
        </header>

        <section className="studio-block">
          <div className="studio-title"><span className="studio-icon peach"><UploadCloud /></span><div><h3>批量上传图片</h3><p>一次选 300 张 · 自动分批并发 · 单张 20MB</p></div></div>
          <button
            className="dropzone"
            onClick={() => inputRef.current?.click()}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => { event.preventDefault(); addFiles(event.dataTransfer.files); }}
          >
            <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={(event) => event.target.files && addFiles(event.target.files)} />
            <UploadCloud size={28} />
            <strong>{files.length ? `队列里有 ${files.length} 张图片` : "点击选择，或拖进这里"}</strong>
            <span>{files.length ? files.map((file) => file.name).join(" · ") : "支持 JPG、PNG、WebP、GIF、AVIF"}</span>
          </button>
          {files.length > 0 && (
            <div className="queue-summary">
              <div className="file-strip">
                {files.slice(0, 6).map((file) => <img key={`${file.name}-${file.lastModified}`} src={URL.createObjectURL(file)} alt="待上传预览" />)}
                {files.length > 6 && <span>+{files.length - 6}</span>}
              </div>
              <button onClick={() => setFiles([])} disabled={busy === "upload"}><Trash2 size={15} /> 清空</button>
            </div>
          )}
          <textarea value={caption} onChange={(event) => setCaption(event.target.value)} placeholder="给这一组图片留句话（可选）" rows={2} />
          <button className="primary-action" onClick={upload} disabled={busy !== null}>
            {busy === "upload" ? <LoaderCircle className="spin" /> : <ArrowUpRight />} {busy === "upload" ? `正在上传 ${uploadProgress}%` : `上传${files.length ? ` ${files.length} 张` : ""}到拾光册`}
          </button>
          {busy === "upload" && <div className="upload-progress"><span style={{ width: `${uploadProgress}%` }} /></div>}
        </section>

        <section className="studio-block">
          <div className="studio-title"><span className="studio-icon violet"><Link2 /></span><div><h3>链接解析收藏</h3><p>抖音 / 小红书公开分享链接</p></div></div>
          <textarea
            className="share-input"
            value={shareText}
            onChange={(event) => setShareText(event.target.value)}
            placeholder="把分享文案整段粘贴到这里，例如：复制此链接，打开抖音…… https://v.douyin.com/…"
            rows={5}
          />
          <button className="primary-action violet-action" onClick={importShare} disabled={busy !== null}>
            {busy === "import" ? <LoaderCircle className="spin" /> : <Sparkles />} 自动解析并归档
          </button>
          <p className="tiny-note">会提取公开页面中的标题、封面、图片或视频，并尽量归档到你的 R2。</p>
        </section>

        <button className="logout-button" onClick={onLogout}><LogOut size={17} /> 退出管理模式</button>
      </aside>
    </div>
  );
}
